// app/src/lib/ocr/dates.js — date sanity checks for OCR'd invoices.
//
// A receipt/invoice ISSUE date can never be in the future: you can't hold a
// receipt for a purchase that hasn't happened. When OCR returns a future issue
// date it's a misread (a transposed digit, a wrong year, or a due/issue mix-up),
// so we flag it for correction. NOTE: this rule is for the issue date only — a
// DUE date is routinely in the future (net-30 terms, etc.), so it's never
// checked here.
//
// The future check alone isn't enough: warehouse clubs print an account/resale
// number (Costco's "A44192523", a member or resale-license id) that the OCR
// sometimes mistakes for a date and pins YEARS in the past — e.g. "2023-…",
// which is in the past, so the future rule lets it through. Receipts are scanned
// soon after purchase, so a normal issue date is RECENT: within the current or
// previous week. Anything outside that window (too old, or in the future) is the
// signature of a misread and is flagged for the operator to confirm or correct.
//
// All dates are the parser's canonical YYYY-MM-DD (see ocrVocabularies.mjs).
// Zero-padded ISO date strings compare correctly with plain string `<`/`>`, so
// string comparison handles the future check; the staleness check needs a day
// count, computed via Date.UTC to dodge timezone/DST surprises.

// Two thresholds, because "a bit old" and "impossibly old" deserve different
// treatment (owner rule 2026-08-10):
//
//   > 1 WEEK      → CONFIRM. The date is probably right (a receipt entered a few
//                   days late is routine), so it is kept in the field and the
//                   operator is asked to confirm it before saving. Replacing it
//                   would silently misdate a real receipt.
//   > 6 MONTHS    → SUSPECT. No receipt being entered now is really from last
//                   year; this is the signature of a misread year (a Costco
//                   scan on 2026-06-20 came back 2025-06-19 — right day, junk
//                   year, and it sat a year out of every report). Treated like a
//                   future date: today goes in the field, the scanned value is
//                   handed back for one-tap restore.
export const RECENT_WINDOW_DAYS = 7;
export const SUSPICIOUS_WINDOW_DAYS = 182;

/**
 * Normalize a value to a bare YYYY-MM-DD string, or null if it isn't a real
 * calendar date. Validates the actual date, not just the shape: a well-formed-
 * looking but impossible value (2026-13-45, 2026-02-30) is rejected, as is a
 * non-date the OCR may have stored before the server guard existed. A trailing
 * time component is tolerated and dropped (2026-07-31T00:00:00Z → 2026-07-31).
 */
export function parseISODate(s) {
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  // Reject calendar overflow (Date rolls 2026-02-30 forward to March).
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/** Today's local date as YYYY-MM-DD. `now` is injectable for tests. */
export function todayISO(now) {
  const d = now || new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/**
 * Is an invoice/receipt ISSUE date in the future relative to `todayStr`?
 * Both are compared as date-only ISO strings; a non-ISO or missing date is
 * treated as "not future" (nothing to flag — the field just needs entering).
 * @param {string} dateStr   the parsed issue date
 * @param {string} [todayStr] defaults to today's local date
 */
export function isFutureIssueDate(dateStr, todayStr) {
  const d = parseISODate(dateStr);
  const t = parseISODate(todayStr) || todayISO();
  if (!d) return false;
  return d > t;
}

/** Midnight-UTC epoch ms for a YYYY-MM-DD string (no timezone drift). */
function isoToUTC(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/**
 * Classify an OCR'd issue date against the recent-receipt windows.
 *   - 'future'      — dated after today (a receipt can't be)
 *   - 'ancient'     — older than `suspiciousDays` (~6 months): not credible for a
 *                     receipt being entered now, so read as a misread year
 *   - 'stale'       — older than `windowDays` (a week) but within 6 months:
 *                     plausible, just wants confirming
 *   - 'ok'          — within [today − windowDays, today]
 *   - 'unparseable' — not an ISO date; nothing to check (the field needs entering)
 * `daysAgo` is the whole-day gap from the date to today for every past status
 * (null for 'future'/'unparseable'). All three of 'future', 'ancient' and
 * 'stale' warrant a confirm before saving; see {@link resolveIssueDate} for
 * which of them also get today substituted into the field.
 * @param {string} dateStr    the parsed issue date
 * @param {string} [todayStr] defaults to today's local date
 * @param {number} [windowDays] confirm beyond this, defaults to RECENT_WINDOW_DAYS
 * @param {number} [suspiciousDays] treat as a misread beyond this, defaults to SUSPICIOUS_WINDOW_DAYS
 * @returns {{ status:'future'|'ancient'|'stale'|'ok'|'unparseable', daysAgo:number|null }}
 */
export function classifyIssueDate(dateStr, todayStr, windowDays = RECENT_WINDOW_DAYS, suspiciousDays = SUSPICIOUS_WINDOW_DAYS) {
  const d = parseISODate(dateStr);
  const t = parseISODate(todayStr) || todayISO();
  if (!d) return { status: 'unparseable', daysAgo: null };
  if (d > t) return { status: 'future', daysAgo: null };
  const daysAgo = Math.round((isoToUTC(t) - isoToUTC(d)) / 86_400_000);
  if (daysAgo > Math.max(windowDays, suspiciousDays)) return { status: 'ancient', daysAgo };
  if (daysAgo > windowDays) return { status: 'stale', daysAgo };
  return { status: 'ok', daysAgo };
}

/**
 * Should an issue date be flagged for confirmation? True for a future date or
 * any date older than the recent window. A missing/unparseable date is not
 * flagged (it just needs entering). Wrapper over {@link classifyIssueDate}.
 */
export function isOutOfRangeIssueDate(dateStr, todayStr, windowDays = RECENT_WINDOW_DAYS, suspiciousDays = SUSPICIOUS_WINDOW_DAYS) {
  const { status } = classifyIssueDate(dateStr, todayStr, windowDays, suspiciousDays);
  return status === 'future' || status === 'ancient' || status === 'stale';
}

/**
 * Decide the issue date a freshly scanned receipt should OPEN with.
 *
 * A receipt is scanned when it is bought, so today is the right default. The
 * extractor sometimes returns a date that cannot be this receipt's — a
 * warehouse-club membership or account number read as a date pins it years back
 * (a Costco scan on 2026-08-05 came back 2023-08-05: right day, junk year), and
 * a transposed digit can push it into the future. Merely flagging those left the
 * operator retyping a date the app already knew.
 *
 * So a NOT-CREDIBLE date — in the future, or more than six months back — is
 * REPLACED by today, and the scanned value is handed back so the UI can show
 * what was read and offer it as a one-tap restore: the operator entering a
 * genuinely old receipt is a real case and must not be silently overruled.
 *
 * A merely week-plus-old date is NOT replaced. It's usually correct (a receipt
 * entered a few days late), and substituting today there would quietly misdate
 * a real receipt for anyone who didn't notice. It gets confirmed at save time
 * instead — see {@link classifyIssueDate}'s 'stale'.
 *
 * @param {string} ocrDate    the extractor's issue date
 * @param {string} [todayStr] defaults to today's local date
 * @returns {{ date:string, scannedDate:string|null, replaced:boolean }}
 *   date         — what to put in the field
 *   scannedDate  — the rejected value, or null when nothing was replaced
 *   replaced     — whether today was substituted
 */
export function resolveIssueDate(ocrDate, todayStr) {
  const today = parseISODate(todayStr) || todayISO();
  const { status } = classifyIssueDate(ocrDate, today);
  if (status === 'future' || status === 'ancient') {
    return { date: today, scannedDate: parseISODate(ocrDate) || String(ocrDate), replaced: true };
  }
  return { date: parseISODate(ocrDate) || '', scannedDate: null, replaced: false };
}
